import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, ChevronDown } from 'lucide-react';
import { apiService } from '@/lib/api';
import { JobDTO } from '@/types';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { formatInstantInCompanyTimeZone, isPreFeatureSettleDate } from '@/lib/dateUtils';
import { showAlert } from '@/lib/capacitor';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const SETTLED_COLOR = '#16a34a';

interface JobBalanceSettleProps {
  job: JobDTO;
  onJobUpdated?: (updatedJob: JobDTO) => void;
  /** compact: inline row for the payout panel (mobile). Default: titled block for the desktop column. */
  compact?: boolean;
}

/**
 * Balance settlement state (req 8, shown to everyone who can view the job) plus the settle/unsettle
 * control (req 9, gated on jobs.settle-balance; technicians never see it). Any non-canceled job can
 * be settled — completed or not.
 */
export function JobBalanceSettle({ job, onJobUpdated, compact = false }: JobBalanceSettleProps) {
  const { isFeatureAllowed } = usePermissions();
  const { userRole } = useCurrentUser();
  const isTechnician = userRole === 'technician';
  const companyTz = useCompanyTimezone();
  const queryClient = useQueryClient();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const canSettle = !isTechnician && isFeatureAllowed('jobs.settle-balance');
  const isCanceled = job.canceled_at != null;
  const isSettled = job.balance_settled_at != null;
  const balance = job.technician_balance ?? 0;
  const canChange = canSettle && !isCanceled;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const applyJobUpdate = (updatedJob: JobDTO) => {
    queryClient.setQueryData(['job', job.id], updatedJob);
    queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
      if (!existing) return existing;
      const isArray = Array.isArray(existing);
      const items = isArray ? existing : existing.jobs;
      if (!Array.isArray(items)) return existing;
      const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
      return isArray ? updatedItems : { ...existing, jobs: updatedItems };
    });
    onJobUpdated?.(updatedJob);
  };

  const settleMutation = useMutation({
    mutationFn: (settled: boolean) =>
      settled ? apiService.settleJobBalance(job.id) : apiService.unsettleJobBalance(job.id),
    onSuccess: (updatedJob: JobDTO) => applyJobUpdate(updatedJob),
    onError: (error: any) => {
      const msg = error?.response?.data?.details || error?.response?.data?.title || error?.message ||
        'Failed to update settlement status.';
      showAlert(msg, 'Settlement Error');
    },
  });

  const selectSettled = (settled: boolean) => {
    setShowDropdown(false);
    if (settled === isSettled) return;
    settleMutation.mutate(settled);
  };

  const detailText = isSettled
    ? (isPreFeatureSettleDate(job.balance_settled_at)
      ? 'Automatic for past jobs'
      : formatInstantInCompanyTimeZone(job.balance_settled_at, companyTz))
    : null;

  const dropdown = (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => canChange && setShowDropdown(!showDropdown)}
        disabled={settleMutation.isPending || !canChange}
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit transition-all ${canChange ? 'hover:shadow-md cursor-pointer' : 'cursor-default'} ${isSettled ? '' : 'bg-red-100 text-red-700'}`}
        style={isSettled ? { backgroundColor: SETTLED_COLOR, color: '#ffffff', border: 'none' } : { border: 'none' }}
      >
        <span className={canChange ? 'mr-1' : ''}>{isSettled ? 'Settled' : 'Unsettled'}</span>
        {canChange && <ChevronDown className="h-3 w-3 opacity-70" />}
      </button>

      {showDropdown && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 flex flex-col">
          <button
            type="button"
            onClick={() => selectSettled(true)}
            disabled={settleMutation.isPending}
            className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isSettled ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
          >
            <div
              className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold"
              style={{ backgroundColor: SETTLED_COLOR, color: '#ffffff' }}
            >
              Settled
            </div>
          </button>
          <button
            type="button"
            onClick={() => selectSettled(false)}
            disabled={settleMutation.isPending}
            className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${!isSettled ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
          >
            <div className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
              Unsettled
            </div>
          </button>
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <span className="text-sm font-medium text-gray-700">Balance settled:</span>
        <div className="flex flex-col items-end gap-0.5">
          {dropdown}
          {detailText && <span className="text-xs text-gray-500">{detailText}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
      <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        <Lock className="h-4 w-4 text-indigo-500" />
        Technician Balance
      </h3>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-700">
          {balance >= 0 ? 'Company owes tech:' : 'Tech owes company:'}
        </span>
        <span className={`text-sm font-bold ${balance > 0 ? 'text-amber-500' : balance < 0 ? 'text-green-600' : 'text-gray-500'}`}>
          {formatCurrency(Math.abs(balance))}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">{detailText ?? ''}</span>
        {dropdown}
      </div>
    </div>
  );
}
